import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AISettings as AISettingsType, AIProvider } from '../types';
import { getDatabaseService } from '../services/DatabaseFactory';
import { loadPwaAISettings, savePwaAISettings, testAIConnection } from '../services/AIEvaluationFactory';

// Calculate isPWA outside component to avoid re-renders
const IS_PWA = typeof window !== 'undefined' && !('electronAPI' in window);

// PWA-only Settings Component
const PWASettings: React.FC = () => {
  const [refreshingDecks, setRefreshingDecks] = useState(false);
  const [refreshResult, setRefreshResult] = useState<{
    success: boolean;
    imported?: number;
    skipped?: number;
    orphansRemoved?: number;
    dirtyDecks?: { id: string; name: string }[];
    error?: string;
  } | null>(null);
  const [selectedDirtyDecks, setSelectedDirtyDecks] = useState<Set<string>>(new Set());
  const [overwriting, setOverwriting] = useState(false);

  // AI settings state
  const [aiSettings, setAISettings] = useState<AISettingsType>({ provider: 'disabled', temperature: 0.3 });
  const [rememberKey, setRememberKey] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [savingAI, setSavingAI] = useState(false);
  const [testingAI, setTestingAI] = useState(false);
  const [aiTestResult, setAITestResult] = useState<{ success: boolean; error?: string } | null>(null);

  useEffect(() => {
    const stored = loadPwaAISettings();
    setAISettings({ provider: stored.provider, temperature: stored.temperature, apiKey: stored.apiKey });
    setRememberKey(stored.rememberKey);
  }, []);

  const handleAISave = async () => {
    setSavingAI(true);
    setAITestResult(null);
    try {
      savePwaAISettings(aiSettings, rememberKey);
      setAITestResult({ success: true });
      // Clear success message after 2s
      setTimeout(() => setAITestResult(null), 2000);
    } catch {
      setAITestResult({ success: false, error: 'Failed to save settings' });
    } finally {
      setSavingAI(false);
    }
  };

  const handleAITest = async () => {
    setTestingAI(true);
    setAITestResult(null);
    try {
      // Save first so the service uses current settings
      savePwaAISettings(aiSettings, rememberKey);
      const result = await testAIConnection(aiSettings);
      setAITestResult(result);
    } catch (error) {
      setAITestResult({ success: false, error: error instanceof Error ? error.message : 'Unknown error' });
    } finally {
      setTestingAI(false);
    }
  };

  const handleAIProviderChange = (provider: AIProvider) => {
    setAISettings(prev => ({ ...prev, provider }));
    setAITestResult(null);
  };

  const handleAIInputChange = (field: keyof AISettingsType, value: string | number) => {
    setAISettings(prev => ({ ...prev, [field]: value }));
    setAITestResult(null);
  };

  const handleRefreshStarterDecks = async () => {
    setRefreshingDecks(true);
    setRefreshResult(null);
    setSelectedDirtyDecks(new Set());

    try {
      const db = await getDatabaseService();
      if (!db.refreshStarterDecks) {
        setRefreshResult({
          success: false,
          imported: 0,
          skipped: 0,
          error: 'This feature is only available in PWA mode'
        });
        return;
      }
      const result = await db.refreshStarterDecks();
      setRefreshResult(result);

      if (result.success && result.imported > 0 && !result.dirtyDecks?.length) {
        // Reload page after 2 seconds to show new decks
        setTimeout(() => window.location.reload(), 2000);
      }
    } catch (error) {
      setRefreshResult({
        success: false,
        imported: 0,
        skipped: 0,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setRefreshingDecks(false);
    }
  };

  const handleOverwriteDirtyDecks = async () => {
    if (selectedDirtyDecks.size === 0) return;
    setOverwriting(true);

    try {
      const db = await getDatabaseService();
      if (!db.overwriteDirtyDecks) return;
      await db.overwriteDirtyDecks(Array.from(selectedDirtyDecks));
      // Reload to reflect changes
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Failed to overwrite dirty decks:', error);
    } finally {
      setOverwriting(false);
    }
  };

  const toggleDirtyDeck = (id: string) => {
    setSelectedDirtyDecks(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => {
              // WORKAROUND: Use window.location.replace instead of navigate()
              // because History API navigation freezes (React Router issue with PWA)
              window.location.replace('/');
            }}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Manage app data</p>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Starter Decks</h2>
          </div>
          <div className="card-body space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-white">Refresh Starter Decks</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Import new starter decks that have been added to the library. This won't affect your existing decks or progress.
                </p>
              </div>
              <button
                onClick={handleRefreshStarterDecks}
                disabled={refreshingDecks}
                className="btn btn-primary disabled:opacity-50 ml-4 whitespace-nowrap"
              >
                {refreshingDecks ? 'Refreshing...' : 'Refresh Decks'}
              </button>
            </div>

            {refreshResult && (
              <div className={`p-3 rounded-lg ${
                refreshResult.success
                  ? 'bg-green-50 border border-green-200 dark:bg-green-900/20'
                  : 'bg-red-50 border border-red-200 dark:bg-red-900/20'
              }`}>
                <p className={`text-sm ${
                  refreshResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                }`}>
                  {refreshResult.success
                    ? `Imported ${refreshResult.imported ?? 0} deck(s), ${refreshResult.skipped ?? 0} skipped.${
                        (refreshResult.orphansRemoved ?? 0) > 0
                          ? ` Removed ${refreshResult.orphansRemoved} orphaned deck(s).`
                          : ''
                      }${
                        (refreshResult.imported ?? 0) > 0 && !refreshResult.dirtyDecks?.length
                          ? ' Reloading...'
                          : ''
                      }`
                    : `Failed: ${refreshResult.error}`
                  }
                </p>
              </div>
            )}

            {refreshResult?.dirtyDecks && refreshResult.dirtyDecks.length > 0 && (
              <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 dark:bg-yellow-900/20">
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                  The following starter decks have been edited by you and were not refreshed. Select any you'd like to overwrite with the latest version:
                </p>
                <div className="space-y-1 mb-3">
                  {refreshResult.dirtyDecks.map(deck => (
                    <label key={deck.id} className="flex items-center space-x-2 text-sm text-yellow-700 dark:text-yellow-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDirtyDecks.has(deck.id)}
                        onChange={() => toggleDirtyDeck(deck.id)}
                        className="rounded border-yellow-400"
                      />
                      <span>{deck.name}</span>
                    </label>
                  ))}
                </div>
                <button
                  onClick={handleOverwriteDirtyDecks}
                  disabled={selectedDirtyDecks.size === 0 || overwriting}
                  className="btn btn-sm bg-yellow-600 text-white hover:bg-yellow-700 disabled:opacity-50"
                >
                  {overwriting ? 'Overwriting...' : `Overwrite ${selectedDirtyDecks.size} selected deck(s)`}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* AI Evaluation Settings */}
        <div className="space-y-4 mt-6">
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Answer Evaluation</h2>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {([
                  { value: 'disabled', label: 'Disabled', desc: 'No AI evaluation' },
                  { value: 'openai', label: 'OpenAI', desc: 'GPT-4 via OpenAI API' },
                  { value: 'anthropic', label: 'Anthropic', desc: 'Claude via Anthropic API' },
                ] as { value: AIProvider; label: string; desc: string }[]).map(({ value, label, desc }) => (
                  <label
                    key={value}
                    className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-colors ${
                      aiSettings.provider === value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pwa-provider"
                      value={value}
                      checked={aiSettings.provider === value}
                      onChange={() => handleAIProviderChange(value)}
                      className="sr-only"
                    />
                    <span className="font-medium text-gray-900 dark:text-white text-sm">{label}</span>
                    <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">{desc}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {(aiSettings.provider === 'openai' || aiSettings.provider === 'anthropic') && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {aiSettings.provider === 'openai' ? 'OpenAI' : 'Anthropic'} Settings
                </h2>
              </div>
              <div className="card-body space-y-4">
                <div className="form-group">
                  <label className="form-label">API Key</label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={aiSettings.apiKey || ''}
                      onChange={(e) => handleAIInputChange('apiKey', e.target.value)}
                      className="form-input pr-10"
                      placeholder={aiSettings.provider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {showApiKey ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                  <label className="flex items-center space-x-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberKey}
                      onChange={(e) => setRememberKey(e.target.checked)}
                      className="rounded border-gray-300"
                    />
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      Remember API key (stored in browser localStorage — uncheck for session-only)
                    </span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="form-label">Temperature: {aiSettings.temperature ?? 0.3}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={aiSettings.temperature ?? 0.3}
                    onChange={(e) => handleAIInputChange('temperature', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Conservative (0.0)</span>
                    <span>Creative (1.0)</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {aiSettings.provider !== 'disabled' && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Test Connection</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Verify your API key works before saving
                    </p>
                  </div>
                  <button
                    onClick={handleAITest}
                    disabled={testingAI}
                    className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testingAI ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Testing...</span>
                      </div>
                    ) : (
                      'Test Connection'
                    )}
                  </button>
                </div>

                {aiTestResult && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    aiTestResult.success
                      ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                      : 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                  }`}>
                    <p className={`text-sm ${
                      aiTestResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
                    }`}>
                      {aiTestResult.success
                        ? 'Connection successful! AI evaluation is ready to use.'
                        : `Connection failed: ${aiTestResult.error}`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleAISave}
              disabled={savingAI}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {savingAI ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save AI Settings'
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

// Electron-only Settings Component
const ElectronSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<AISettingsType>({
    provider: 'disabled',
    temperature: 0.3,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string } | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const currentSettings = await window.electronAPI.getAISettings();
        setSettings(currentSettings);
      } catch (error) {
        console.error('Failed to load AI settings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    
    try {
      await window.electronAPI.updateAISettings(settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    
    try {
      // Save settings first to test with current configuration
      await window.electronAPI.updateAISettings(settings);
      const result = await window.electronAPI.testAIConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({ 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
    } finally {
      setTesting(false);
    }
  };

  const handleProviderChange = (provider: AIProvider) => {
    setSettings(prev => ({ ...prev, provider }));
    setTestResult(null);
  };

  const handleInputChange = (field: keyof AISettingsType, value: string | number) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setTestResult(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-2">
          <div className="spinner"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                AI Settings
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Configure AI providers for answer evaluation
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* AI Provider Selection */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">AI Provider</h2>
            </div>
            <div className="card-body space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { value: 'disabled', label: 'Disabled', desc: 'No AI evaluation' },
                  { value: 'local', label: 'Local (Ollama)', desc: 'Run locally with Ollama' },
                  { value: 'openai', label: 'OpenAI', desc: 'GPT-4 via OpenAI API' },
                  { value: 'anthropic', label: 'Anthropic', desc: 'Claude via Anthropic API' },
                ].map(({ value, label, desc }) => (
                  <label
                    key={value}
                    className={`relative flex flex-col p-4 border rounded-lg cursor-pointer transition-colors ${
                      settings.provider === value
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="provider"
                      value={value}
                      checked={settings.provider === value}
                      onChange={() => handleProviderChange(value as AIProvider)}
                      className="sr-only"
                    />
                    <span className="font-medium text-gray-900 dark:text-white text-sm">
                      {label}
                    </span>
                    <span className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {desc}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Local Settings */}
          {settings.provider === 'local' && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Local AI Settings</h2>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">Model</label>
                    <input
                      type="text"
                      value={settings.localModel || ''}
                      onChange={(e) => handleInputChange('localModel', e.target.value)}
                      className="form-input"
                      placeholder="codellama:7b"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ollama model name (e.g., codellama:7b, qwen2.5-coder:1.5b)
                    </p>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Endpoint</label>
                    <input
                      type="text"
                      value={settings.apiEndpoint || ''}
                      onChange={(e) => handleInputChange('apiEndpoint', e.target.value)}
                      className="form-input"
                      placeholder="http://localhost:11434"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ollama server endpoint
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* API Key Settings */}
          {(settings.provider === 'openai' || settings.provider === 'anthropic') && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {settings.provider === 'openai' ? 'OpenAI' : 'Anthropic'} Settings
                </h2>
              </div>
              <div className="card-body space-y-4">
                <div className="form-group">
                  <label className="form-label">API Key</label>
                  <div className="relative">
                    <input
                      type={showApiKey ? 'text' : 'password'}
                      value={settings.apiKey || ''}
                      onChange={(e) => handleInputChange('apiKey', e.target.value)}
                      className="form-input pr-10"
                      placeholder={
                        settings.provider === 'openai' 
                          ? 'sk-...' 
                          : 'sk-ant-...'
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {showApiKey ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        )}
                      </svg>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Your API key is encrypted and stored securely
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Advanced Settings */}
          {settings.provider !== 'disabled' && (
            <div className="card">
              <div className="card-header">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Advanced Settings</h2>
              </div>
              <div className="card-body space-y-4">
                <div className="form-group">
                  <label className="form-label">Temperature: {settings.temperature}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.temperature || 0.3}
                    onChange={(e) => handleInputChange('temperature', parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Conservative (0.0)</span>
                    <span>Creative (1.0)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Controls randomness in AI responses. Lower values are more consistent.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Test Connection */}
          {settings.provider !== 'disabled' && (
            <div className="card">
              <div className="card-body">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Test Connection</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Verify your AI provider configuration
                    </p>
                  </div>
                  <button
                    onClick={handleTestConnection}
                    disabled={testing}
                    className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {testing ? (
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 border border-gray-600 border-t-transparent rounded-full animate-spin"></div>
                        <span>Testing...</span>
                      </div>
                    ) : (
                      'Test Connection'
                    )}
                  </button>
                </div>
                
                {testResult && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    testResult.success 
                      ? 'bg-green-50 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
                      : 'bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
                  }`}>
                    <p className={`text-sm ${
                      testResult.success 
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-red-700 dark:text-red-300'
                    }`}>
                      {testResult.success 
                        ? 'Connection successful! AI evaluation is ready to use.'
                        : `Connection failed: ${testResult.error}`
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions - Only show for Electron mode */}
          {(
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => navigate('/')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Settings'
              )}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main component - routes to appropriate version
const AISettings: React.FC = () => {
  console.log('[AISettings] Router rendering, IS_PWA:', IS_PWA);
  const Component = IS_PWA ? PWASettings : ElectronSettings;
  return <Component />;
};

export default AISettings;